import { useCallback, useState } from 'react';

import {
  getPresignedUrl,
  uploadFileToS3,
  validateFile,
} from '@/shared/services/s3';
import {
  ThumbnailUploadState,
  UseThumbnailUploadReturn,
} from '@/shared/types/s3Types';

export const useThumbnailUpload = (
  thumbnailUrls: string[],
  onThumbnailsChange: (urls: string[]) => void,
): UseThumbnailUploadReturn => {
  const [uploadState, setUploadState] = useState<ThumbnailUploadState>({
    isLoading: false,
    error: null,
    editingIndex: null,
  });

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];

      const validation: { isValid: boolean; error?: string } =
        validateFile(file);
      if (!validation.isValid) {
        setUploadState((prev) => ({
          ...prev,
          error: validation.error || 'Invalid file',
        }));
        alert(validation.error);
        return;
      }

      setUploadState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const presignedUrl = await getPresignedUrl(file);
        const uploadedUrl = await uploadFileToS3(presignedUrl, file);

        const newThumbnails = [...thumbnailUrls, uploadedUrl];
        onThumbnailsChange(newThumbnails);

        setUploadState((prev) => ({ ...prev, isLoading: false, error: null }));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An error occurred while uploading.';
        setUploadState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        alert(errorMessage);
      }
    },
    [thumbnailUrls, onThumbnailsChange],
  );

  const handleFileEdit = useCallback(
    async (files: FileList | null, index: number) => {
      if (!files || files.length === 0) return;

      const file = files[0];

      const validation = validateFile(file);
      if (!validation.isValid) {
        setUploadState((prev) => ({
          ...prev,
          error: validation.error || 'Invalid file',
        }));
        alert(validation.error);
        return;
      }

      setUploadState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const presignedUrl = await getPresignedUrl(file);
        const uploadedUrl = await uploadFileToS3(presignedUrl, file);

        const newThumbnails = [...thumbnailUrls];
        newThumbnails[index] = uploadedUrl;
        onThumbnailsChange(newThumbnails);

        setUploadState((prev) => ({
          ...prev,
          isLoading: false,
          error: null,
          editingIndex: null,
        }));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An error occurred while editing.';
        setUploadState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }));
        alert(errorMessage);
      }
    },
    [thumbnailUrls, onThumbnailsChange],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newThumbnails = thumbnailUrls.filter((_, i) => i !== index);
      onThumbnailsChange(newThumbnails);
    },
    [thumbnailUrls, onThumbnailsChange],
  );

  const handleImageClick = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  const setEditingIndex = useCallback((index: number | null) => {
    setUploadState((prev) => ({ ...prev, editingIndex: index }));
  }, []);

  return {
    uploadState,
    handleFileUpload,
    handleFileEdit,
    handleDelete,
    handleImageClick,
    setEditingIndex,
  };
};
